import { useCallback, useEffect, useRef } from 'react';
import { useAuth, isAuthRedirectInProgress } from './useAuth';
import { apiConfig } from '@/config/authConfig';
import { useSessionStatus, AUTH_ERROR, SessionExpiredError } from '@/context/AuthSessionContext';
import type { ApiResponse } from '@ip/shared-types';

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

/** Absolute URLs pass through; everything else is relative to the API base. */
function resolveUrl(endpoint: string): string {
  return endpoint.startsWith('http') ? endpoint : `${apiConfig.baseUrl}${endpoint}`;
}

export function useApi() {
  const { getAccessToken } = useAuth();
  const { sessionExpired, setSessionExpired } = useSessionStatus();

  // Read the latch without making it a dependency of every request callback
  // (it flips on failure and back on recovery — re-creating `get`/`post` on
  // each flip would churn every consumer's effects).
  const sessionExpiredRef = useRef(sessionExpired);
  useEffect(() => {
    sessionExpiredRef.current = sessionExpired;
  }, [sessionExpired]);

  /**
   * Single authenticated-request path shared by every verb below
   * (2026-09-07 — previously five near-identical copies of this block).
   *
   * Returns the Response, or null when the session could not be established
   * (token unavailable, or still 401 after a forced refresh); callers turn
   * null into AUTH_ERROR / SessionExpiredError. `sessionExpired` is flipped
   * TRUE here on failure and, crucially, back to FALSE the moment a request
   * succeeds with a token: before this fix nothing ever unlatched it (the
   * only reset lived after `await login()` in SessionExpiredModal, which is
   * dead code because loginRedirect navigates away), so one transient token
   * failure pinned the "Sesja wygasła" modal for the rest of the tab's life.
   */
  const requestWithAuth = useCallback(
    async (makeRequest: (bearerToken: string) => Promise<Response>): Promise<Response | null> => {
      const token = await getAccessToken();
      if (!token) {
        // Unless the failure ALREADY started an interactive redirect: the page
        // is on its way to AAD, and raising the modal over it invites the user
        // to click "Zaloguj się ponownie", which wipes the PKCE verifier and
        // request params of the redirect in flight (2026-09-07).
        if (!isAuthRedirectInProgress()) {
          setSessionExpired(true);
        }
        return null;
      }

      let response = await makeRequest(token);

      // 401 retry: force-refresh token and try once more
      if (response.status === 401) {
        const freshToken = await getAccessToken({ forceRefresh: true });
        if (freshToken) {
          response = await makeRequest(freshToken);
        }
        if (!freshToken || response.status === 401) {
          if (!isAuthRedirectInProgress()) {
            setSessionExpired(true);
          }
          return null;
        }
      }

      // We reached the backend with an accepted token — any earlier latch is
      // stale. Guarded so the common case causes no re-render.
      if (sessionExpiredRef.current) {
        sessionExpiredRef.current = false;
        setSessionExpired(false);
      }

      return response;
    },
    [getAccessToken, setSessionExpired],
  );

  const fetchWithAuth = useCallback(
    async <T>(endpoint: string, options: FetchOptions = {}): Promise<ApiResponse<T>> => {
      try {
        const url = resolveUrl(endpoint);
        const response = await requestWithAuth((bearerToken) =>
          fetch(url, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${bearerToken}`,
              ...options.headers,
            },
            cache: 'no-store',
          }),
        );

        if (!response) {
          return { success: false, error: AUTH_ERROR };
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          return {
            success: false,
            error: errorData.message || `HTTP error: ${response.status}`,
            status: response.status,
          };
        }

        // Handle 204 No Content (empty response body)
        if (response.status === 204) {
          return {
            success: true,
            data: undefined as T,
          };
        }

        const data = await response.json();
        return {
          success: true,
          data,
        };
      } catch (error) {
        console.error('API request failed:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
      }
    },
    [requestWithAuth]
  );

  const get = useCallback(
    <T>(endpoint: string) => fetchWithAuth<T>(endpoint, { method: 'GET' }),
    [fetchWithAuth]
  );

  const post = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchWithAuth<T>(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    [fetchWithAuth]
  );

  const put = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchWithAuth<T>(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    [fetchWithAuth]
  );

  const del = useCallback(
    <T>(endpoint: string) => fetchWithAuth<T>(endpoint, { method: 'DELETE' }),
    [fetchWithAuth]
  );

  const patch = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchWithAuth<T>(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    [fetchWithAuth]
  );

  // Send FormData (file uploads) — no Content-Type header: the browser sets
  // the multipart boundary itself.
  const sendFormData = useCallback(
    async <T>(method: 'POST' | 'PUT', endpoint: string, formData: FormData): Promise<ApiResponse<T>> => {
      try {
        const url = resolveUrl(endpoint);
        const response = await requestWithAuth((bearerToken) =>
          fetch(url, {
            method,
            headers: { Authorization: `Bearer ${bearerToken}` },
            body: formData,
          }),
        );

        if (!response) {
          return { success: false, error: AUTH_ERROR };
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          return {
            success: false,
            error: errorData.message || `HTTP error: ${response.status}`,
            status: response.status,
          };
        }

        const data = response.status === 204 ? (undefined as T) : ((await response.json()) as T);
        return { success: true, data };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        };
      }
    },
    [requestWithAuth],
  );

  const postFormData = useCallback(
    <T>(endpoint: string, formData: FormData) => sendFormData<T>('POST', endpoint, formData),
    [sendFormData]
  );

  const putFormData = useCallback(
    <T>(endpoint: string, formData: FormData) => sendFormData<T>('PUT', endpoint, formData),
    [sendFormData],
  );

  // Fetch a raw blob (file downloads) — throws SessionExpiredError so callers
  // can distinguish an expired session from a failed download.
  const fetchBlob = useCallback(
    async (method: 'GET' | 'POST', endpoint: string, body?: unknown): Promise<Blob> => {
      const url = resolveUrl(endpoint);
      const response = await requestWithAuth((bearerToken) =>
        fetch(url, {
          method,
          headers:
            method === 'POST'
              ? { 'Content-Type': 'application/json', Authorization: `Bearer ${bearerToken}` }
              : { Authorization: `Bearer ${bearerToken}` },
          ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
        }),
      );

      if (!response) {
        throw new SessionExpiredError();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error: ${response.status}`);
      }

      return response.blob();
    },
    [requestWithAuth],
  );

  const postRaw = useCallback(
    (endpoint: string, body: unknown) => fetchBlob('POST', endpoint, body),
    [fetchBlob]
  );

  const getBlob = useCallback(
    (endpoint: string) => fetchBlob('GET', endpoint),
    [fetchBlob]
  );

  return {
    get,
    post,
    put,
    patch,
    delete: del,
    postRaw,
    getBlob,
    postFormData,
    putFormData,
    fetchWithAuth,
  };
}
